import React, {useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import {Link, withRouter} from 'react-router-dom';
import {
	BUTTON_COLOR,
	BUTTON_STYLE,
	DATE_PICKER_STYLE,
	ELEMENT_TYPE,
	FILTER_SECTION,
	FILTER_TYPE,
	GLOBAL_FILTER_FIELD,
	GLOBAL_FILTER_OPERATOR,
	HIDDEN_FEATURES,
	MODULE_TYPES,
	REPORT_GROUPINGS,
} from '../../../../constants';
import {FILTER_SECTIONS} from '../../../shared/components/filters/dropdown_section';
import {getFiltersAlphabetically} from '../../../shared/components/filters/filter_util';
import {buildHeaderBar} from '../../../shared/components/headers/header-bar/header_bar';
import {
	TopHeaderAllReportsWrapper,
	TopHeaderBar,
	TopHeaderBarWrapper,
} from '../../../shared/components/headers/top-header-bar/TopHeaderBar';
import {useGenericReportContext} from '../../saved-report/GenericReportContext';
import {
	filterObjectEyeOptionsWithCustomFields,
	theEyeOptionsFilteredWithCustomFields,
	theEyeOptionsNoGrouping,
} from './TaskReportTheEyeUtil';
import TaskReportTable, {TaskReportTableV2Query} from './TaskReportTableV2';
import {
	getSearchQueryFiltersValue,
	getSecondDropdownOptionsByGroupings,
	noGroupingFirstDropdownOptions,
} from './TaskReportUtil';
import ForecastQueryRenderer from '../../../../ForecastQueryRenderer';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import hash from 'object-hash';
import Util from '../../../shared/util/util';
import {isFeatureHidden} from '../../../shared/util/CompanySetupUtil';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import tracking from '../../../../tracking';
import {useTrackPage} from '../../../../tracking/amplitude/hooks/useTrackPage';
import {hasModule} from '../../../shared/util/ModuleUtil';
import {trackCSVExport, trackEvent} from '../../../../tracking/amplitude/TrackingV2';

const TaskReportPageV2 = ({viewer}) => {
	const intl = useIntl();
	const {
		reportName,
		startDate,
		handleSetStartDate,
		endDate,
		handleSetEndDate,
		// eslint-disable-next-line no-unused-vars
		theEyeOptionsChecked,
		setTheEyeOptionsChecked,
		filterValue,
		firstDropdownValue,
		handleSetFirstDropdownValue,
		secondDropdownValue,
		handleSetSecondDropdownValue,
		handleSetFilterValue,
		handleSetFilters,
		// eslint-disable-next-line no-unused-vars
		getBottomReportHeader,
		getTopReportHeader,
		isSharedView,
		reportId,
		reportShares,
		sharedId,
		getHasOwnerPermission,
		reportOwner,
		privateAccess,
		canPersonShare,
	} = useGenericReportContext();
	const hasOwnerPermission = getHasOwnerPermission(viewer.actualPersonId);
	const canShare = canPersonShare(viewer.actualPersonId);

	const defaultSearchQueryFilters = [
		{field: GLOBAL_FILTER_FIELD.WITH_TASKS, operator: GLOBAL_FILTER_OPERATOR.IS, value: true.toString()},
		{
			field: GLOBAL_FILTER_FIELD.START_DATE,
			operator: GLOBAL_FILTER_OPERATOR.GREATER_OR_EQUAL,
			value: [startDate.format('YYYY-MM-DD')],
		},
		{
			field: GLOBAL_FILTER_FIELD.END_DATE,
			operator: GLOBAL_FILTER_OPERATOR.LESS_OR_EQUAL,
			value: [endDate.format('YYYY-MM-DD')],
		},
	];
	const initialSearchQueryFilters = getSearchQueryFiltersValue(defaultSearchQueryFilters, filterValue);
	const [searchQueryFilters, setSearchQueryFilters] = useState(initialSearchQueryFilters);

	const customFieldDefinitions =
		hasModule(MODULE_TYPES.CUSTOM_FIELDS) || isSharedView
			? viewer.reportData.company.reportCustomFieldDefinitions
					.filter(n => n.entityType === 'TASK')
					.sort((a, b) => (a.displayName > b.displayName ? 1 : -1))
			: [];

	// Handle of grouping in Task Report
	const groupings = [];

	if (firstDropdownValue !== null) {
		groupings.push(firstDropdownValue);
		if (secondDropdownValue !== null) {
			groupings.push(secondDropdownValue);
		}
	}
	groupings.push(REPORT_GROUPINGS.TASK);

	//Handling of showing of columns
	const enabledOptions = filterObjectEyeOptionsWithCustomFields(
		theEyeOptionsChecked,
		groupings,
		isSharedView,
		customFieldDefinitions
	);

	useMemo(() => {
		setTheEyeOptionsChecked(enabledOptions);
	}, [...groupings, hash(theEyeOptionsChecked)]);

	useTrackPage('Standard Task Report');

	useEffect(() => {
		if (!isSharedView && !Util.AuthorizeViewerAccess('task-report')) {
			history.push('/not-authorized');
		} else {
			tracking.trackPage('Standard Task Report');
		}
	}, []);

	// Options for grouping
	const firstDropdownOptions = noGroupingFirstDropdownOptions(intl);
	const [secondDropdownOptions, setSecondDropdownOptions] = useState(
		getSecondDropdownOptionsByGroupings(firstDropdownValue, intl)
	);

	/**
	 * This method is called when the grouping option is changed.
	 * @param option The selected grouping option.
	 */
	const handleFirstDropdownChange = option => {
		trackEvent('First Grouping', 'Changed', {value: option?.value});
		handleSetFirstDropdownValue(option.value);
		handleSetSecondDropdownValue(null);
		setSecondDropdownOptions(getSecondDropdownOptionsByGroupings(option.value, intl));
	};

	const handleSecondDropdownChange = option => {
		trackEvent('Second Grouping', 'Changed', {value: option?.value});
		handleSetSecondDropdownValue(option.value);
	};

	/**
	 * This will handle changes in the selected dates.
	 *
	 * It will filter the tasks on the end-date between the two dates.
	 *
	 * @param newStartDate The new start date
	 * @param newEndDate The new end date
	 */
	const handleDateRangeChange = (newStartDate, newEndDate) => {
		handleSetStartDate(newStartDate);
		handleSetEndDate(newEndDate);

		const updatedSearchQueryFilters = searchQueryFilters.map(filter => {
			const updatedFilterObject = {
				field: filter.field,
				operator: filter.operator,
			};
			if (filter.field === GLOBAL_FILTER_FIELD.START_DATE) {
				updatedFilterObject.value = [newStartDate.format('YYYY-MM-DD')];
			} else if (filter.field === GLOBAL_FILTER_FIELD.END_DATE) {
				updatedFilterObject.value = [newEndDate.format('YYYY-MM-DD')];
			} else {
				updatedFilterObject.value = filter.value;
			}
			return updatedFilterObject;
		});
		setSearchQueryFilters(updatedSearchQueryFilters);
		handleSetFilters(updatedSearchQueryFilters);
	};

	/**
	 * Search query used for filtering.
	 */
	const searchQuery = {
		filters: searchQueryFilters,
	};

	const handleCsvDownload = () => {
		trackCSVExport('Task Report');
		showModal({
			type: MODAL_TYPE.DOWNLOADING_QUERY_DATA_TASK,
			//you can remove the searchQuery and viewer after PR approved
			searchQuery: searchQuery,
			viewer: viewer,
			searchQueryFilters: searchQueryFilters,
			enabledColumns: enabledOptions,
			filterValue: filterValue,
			reportName,
			customFieldDefinitions,
		});
	};

	/**
	 * This method is called when the filter is changing.
	 *
	 * @param filters The selected filters.
	 */
	const onFiltersChange = filters => {
		handleSetFilterValue(filters);
		const updatedFilters = getSearchQueryFiltersValue(defaultSearchQueryFilters, filters);
		handleSetFilters(updatedFilters);
		setSearchQueryFilters(updatedFilters);
	};

	const openShareModal = () => {
		if (hasFeatureFlag('reports_redesign') && !hasFeatureFlag('reports_redesign_inverted')) {
			showModal({
				type: MODAL_TYPE.SHARE_REPORT_MODAL,
				reportId: reportId,
				reportSharesInput: reportShares.edges.map(reportShare => {
					return {
						email: reportShare.node.user.email,
						sharedId: reportShare.node.id,
					};
				}),
				privateAccessInput: privateAccess,
				reportOwner: reportOwner,
			});
		} else {
			showModal({
				type: MODAL_TYPE.EXTERNAL_REPORT_SHARE,
				reportShares: reportShares.edges.map(reportShare => {
					return {
						email: reportShare.node.user.email,
						sharedId: reportShare.node.id,
					};
				}),
				reportId: reportId,
			});
		}
	};

	/**
	 * Creates the title bar with project name (editable)
	 */
	const getHeaderTitleBar = () => {
		let content = [];
		if (hasOwnerPermission) {
			const reportNameProps = {
				defaultTitle:
					intl.formatMessage({id: 'project_overview.task_overview'}) +
					', ' +
					intl.formatMessage({id: 'common.untitled'}),
				placeholder: intl.formatMessage({id: 'time_reg_report.report_name'}),
			};
			content.push(...getTopReportHeader(reportNameProps));
		}

		return (
			<>
				<TopHeaderAllReportsWrapper>
					<Link to={'/reports'}>
						<FormattedMessage id="common.all_reports" />
					</Link>
				</TopHeaderAllReportsWrapper>
				<TopHeaderBarWrapper>
					<TopHeaderBar title={hasOwnerPermission ? null : reportName} content={content} />
				</TopHeaderBarWrapper>
			</>
		);
	};

	const getSharedViewHeaderTitleBar = () => {
		const sDate = intl.formatDate(startDate, {year: 'numeric', month: 'short', day: 'numeric'});
		const eDate = intl.formatDate(endDate, {year: 'numeric', month: 'short', day: 'numeric'});
		const title = `${reportName} (${sDate} - ${eDate})`;

		return (
			<TopHeaderBarWrapper>
				<TopHeaderBar title={title} content={[]} />
			</TopHeaderBarWrapper>
		);
	};

	/**
	 * Creates the Navigation bar, with:
	 *
	 * - Date picker
	 * - CSV downloader
	 * - Groupings
	 * - Eye options
	 * - Filtering
	 */
	const getNavBar = () => {
		const leftContent = [];
		const rightContent = [];

		leftContent.push({
			type: ELEMENT_TYPE.DATE_PICKER,
			userpilot: 'navbar-date-picker',
			isNewDateRange: true,
			startDate: startDate,
			endDate: endDate,
			handleDateRangeChange: (startDate, endDate) => handleDateRangeChange(startDate, endDate),
			colorInherited: true,
			compactShowYear: true,
			datePickerStyle: DATE_PICKER_STYLE.SLIM,
			calendarOffsetX: 0,
			clearable: false,
		});

		rightContent.push({
			type: ELEMENT_TYPE.CSV,
			callback: handleCsvDownload,
		});

		/**
		 * Add grouping options
		 */
		rightContent.push({
			sameTypeOrder: 1,
			type: ELEMENT_TYPE.DROPDOWN,
			userpilot: 'taskreport-first-grouping',
			dropdownOptions: firstDropdownOptions,
			value: firstDropdownValue,
			callback: option => {
				handleFirstDropdownChange(option);
			},
			style: BUTTON_STYLE.OUTLINE,
			color: BUTTON_COLOR.PURPLE,
		});

		/**
		 * Add second grouping options
		 */
		if (secondDropdownOptions !== null) {
			rightContent.push({
				sameTypeOrder: 2,
				type: ELEMENT_TYPE.DROPDOWN,
				dropdownOptions: secondDropdownOptions,
				value: secondDropdownValue,
				callback: option => {
					handleSecondDropdownChange(option);
				},
				style: BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.PURPLE,
			});
		}

		/**
		 * Add 'eye' options
		 */
		const eyeProps = {
			eyeOptions: theEyeOptionsFilteredWithCustomFields(
				theEyeOptionsNoGrouping(),
				groupings,
				isSharedView,
				customFieldDefinitions
			),
		};

		const additionalProps = {eyeProps};
		const filteredHeaderElements = getBottomReportHeader(additionalProps);
		rightContent.push(...filteredHeaderElements);

		if (!(hasFeatureFlag('reports_redesign') && !hasFeatureFlag('reports_redesign_inverted'))) {
			rightContent.push({
				type: ELEMENT_TYPE.BUTTON,
				text: 'Share',
				callback: openShareModal,
				style: BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.PURPLE,
				disabled: !canShare.isShareable,
				tooltipProps: {
					infoText: canShare.cantShareReason,
				},
			});
		}

		/**
		 * Available Filters
		 */
		const taskFilters = [
			FILTER_TYPE.APPROVAL_STATUS,
			FILTER_TYPE.CLIENT,
			FILTER_TYPE.PERSON,
			FILTER_TYPE.PROJECT,
			FILTER_TYPE.ROLE,
			FILTER_TYPE.LABEL,
			FILTER_TYPE.STATUS_CATEGORY,
			FILTER_TYPE.SPRINT_CATEGORY,
			FILTER_TYPE.TEAM,
			FILTER_TYPE.INDICATOR_FILTERED,
			FILTER_TYPE.RECENT_ACTIVITY,
			FILTER_TYPE.PHASE_CATEGORY,
			FILTER_TYPE.PROJECT_STAGE,
			FILTER_TYPE.DEPENDENCIES,
			FILTER_TYPE.OWNER,
			FILTER_TYPE.DEADLINE,
			...(isFeatureHidden(HIDDEN_FEATURES.REVENUE) ? [] : [FILTER_TYPE.PROJECT_TYPE]),
		];

		/**
		 * Add filter selector
		 */
		rightContent.push({
			type: ELEMENT_TYPE.FILTER_V4,
			operatorOptions: {
				allowExclude: true,
				allowRequireAll: true,
			},
			defaultSection: FILTER_SECTIONS.TASKS,
			projectFilters: [],
			peopleFilters: [],
			timeRegFilters: [],
			taskFilters: getFiltersAlphabetically(taskFilters, intl.formatMessage),
			viewer: viewer,
			filterSection: FILTER_SECTION.TASK_REPORT,
			appliedFiltersName: `task-report-filters`,
			noMenu: false,
			useSavedReport: true,
			preAppliedFilters: filterValue,
			onFiltersChange,
			userpilot: 'report-filter-button',
		});
		return buildHeaderBar(leftContent, rightContent);
	};

	return (
		<>
			{isSharedView ? getSharedViewHeaderTitleBar() : getHeaderTitleBar()}
			{!isSharedView && getNavBar()}

			<ForecastQueryRenderer
				key="query-render-taskReportTable"
				query={TaskReportTableV2Query}
				// This is basically initialVariables
				variables={{
					searchQuery,
					reportId,
					sharedId,
				}}
				renderIfNotLoggedIn={true}
				render={relayProps => {
					return (
						<TaskReportTable
							{...relayProps}
							relayProps={relayProps}
							enabledColumns={enabledOptions}
							groupings={groupings}
							searchQuery={searchQuery}
							currency={viewer.reportData.company.currency}
							customFieldDefinitions={customFieldDefinitions}
						/>
					);
				}}
			/>
		</>
	);
};

export const taskReportPageV2Query = graphql`
	query TaskReportPageV2_Query($savedReportId: String, $sharedId: String) {
		viewer {
			actualPersonId
			component(name: "task_report_page_v2")
			...TaskReportPageV2_viewer @arguments(savedReportId: $savedReportId, sharedId: $sharedId)
		}
	}
`;

export default withRouter(
	createFragmentContainer(TaskReportPageV2, {
		viewer: graphql`
			fragment TaskReportPageV2_viewer on Viewer
			@argumentDefinitions(savedReportId: {type: "String"}, sharedId: {type: "String"}) {
				actualPersonId
				id
				email
				filters(first: 10000, filterSection: TASK_REPORT) @connection(key: "Viewer_filters", filters: []) {
					edges {
						node {
							id
							name
							section
							value
							updatedAt
						}
					}
				}
				reportData(id: $savedReportId, sharedId: $sharedId) {
					company {
						currency
						reportCustomFieldDefinitions {
							id
							key
							entityType
							displayName
						}
					}
				}
			}
		`,
	})
);
